import React, { useRef, useState } from 'react'; // Import useRef and useState
import { Draggable } from '@hello-pangea/dnd';
import CardLabels from './CardLabels';
import CardMetaInfo from './CardMeta';
import CardQuickActionsMenu from './card/CardQuickActionsMenu'; // Import the new menu
import {
    CardContainer,
    CardCoverImage, // New import
    CardContent,    // New import
    CardTitle,
    CardFooter,
    AssigneeAvatar,
} from '../styles/Card.styles';

const Card = ({
    card,
    index,
    onClick,
    labels = [],
    areLabelsExpanded,
    onToggleLabels,
    projectMembers = {},
    enableGithubLinks, // Add new prop
    // card.coverImageBase64 will be expected here if a cover is set
    projectId,      // New: Accept projectId
    columnId,       // New: Accept columnId
    allColumns,     // New: Accept allColumns
    columnOrder,    // New: Accept columnOrder
    projectLabels   // New: Accept projectLabels
}) => {
    const quickActionsMenuWrapperRef = useRef(null);
    const [isQuickMenuInteracting, setIsQuickMenuInteracting] = useState(false); // New state

    const assignee = card.assigneeId ? projectMembers[card.assigneeId] : null;
    const hasLabels = card.labelIds?.length > 0 || card.priority;
    const hasGithubLinkAndFeatureEnabled = enableGithubLinks && card.githubUrl;
    // Renamed to avoid conflict if hasMetaContent is used differently elsewhere or for clarity
    const hasActualCardMetaContent = (card.comments?.length > 0) ||
                                (card.checklists && card.checklists.reduce((acc, curr) => acc + curr.items.length, 0) > 0) ||
                                hasGithubLinkAndFeatureEnabled || card.dueDate; // Added dueDate here for completeness

    // Determine if there's any content to display below the cover image or in the card itself
    const hasContentToShow = !!(card.title || hasLabels || hasActualCardMetaContent || assignee);


    const renderAssignee = () => {
        if (!assignee) return null;

        const displayName = assignee.displayName || assignee.email;
        const initial = !assignee.photoURL && (displayName?.[0] || '');

        return (
            <AssigneeAvatar
                src={assignee.photoURL}
                title={displayName}
            >
                {initial}
            </AssigneeAvatar>
        );
    };

    return (
        <Draggable draggableId={card.id} index={index}>
            {(provided, snapshot) => (
                <CardContainer
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    isDragging={snapshot.isDragging}
                    onClick={(e) => {
                        if (isQuickMenuInteracting) { // Check the state
                            // console.log("Card click ignored due to quick menu interaction");
                            return;
                        }
                        if (quickActionsMenuWrapperRef.current && quickActionsMenuWrapperRef.current.contains(e.target)) {
                            // console.log("Card click ignored due to click on menu wrapper");
                            return;
                        }
                        onClick();
                    }}
                >
                    {card.coverImageBase64 ? (
                        <CardCoverImage src={card.coverImageBase64} alt="Card cover" />
                    ) : null}

                    {hasContentToShow ? (
                        <CardContent>
                            {hasLabels ? (
                                <CardLabels
                                    card={card}
                                    labels={labels}
                                    areLabelsExpanded={areLabelsExpanded}
                                    onToggleLabels={onToggleLabels}
                                />
                            ) : null}

                            {card.title ? (
                                <CardTitle hasLabels={hasLabels}>
                                    {card.title}
                                </CardTitle>
                            ) : null}

                            {(assignee || hasActualCardMetaContent) ? (
                                 <CardFooter>
                                    {assignee ? renderAssignee() : null}
                                    {hasActualCardMetaContent ? <CardMetaInfo card={card} enableGithubLinks={enableGithubLinks} /> : null}
                                </CardFooter>
                            ) : null}
                        </CardContent>
                    ) : null}
                    {/* Pass the ref to the CardQuickActionsMenu (which now accepts it via forwardRef) */}
                    <CardQuickActionsMenu
                        ref={quickActionsMenuWrapperRef} // Pass the ref here
                        card={card}
                        projectId={projectId}
                        columnId={columnId}
                        projectMembers={projectMembers}
                        allColumns={allColumns}
                        columnOrder={columnOrder}
                        projectLabels={projectLabels} // Pass projectLabels
                        onInteractionStateChange={setIsQuickMenuInteracting}
                    />
                </CardContainer>
            )}
        </Draggable>
    );
};

export default Card;